// ctxmenu.ts — THE right-click context-menu primitive (Styles x Themes, PR-3b).
//
// OWNER RULING (2026-09-05, program rulings ledger): custom context menus
// EVERYWHERE EXCEPT EDITABLE-TEXT CONTEXTS — inputs, textareas and the
// composer keep the NATIVE menu, because that menu carries spell-check,
// dictionary, paste-and-match-style and the OS text services, and a custom
// menu that replaces it is a capability LOSS, not a theming win.
//
// That exception is encoded HERE, in `attach()`, and nowhere else. It is the
// single place a surface can get the rule wrong, and it now cannot: a call
// site says "menu on this root" and the primitive decides whether the pointer
// is over editable text.
//
// ── what the audit found ──────────────────────────────────────────────────
// The Bridge home is the reference machine: 27/27 right-click sites routed
// through one `ctxmenu.ts`. The Helm was the opposite — SIX registrations in
// THREE files (cardmenu.ts, tabstrip.ts, glance-grid.ts), and every other
// right-clickable surface (dashboard, terminal, agent panes, registers,
// deployments, graph, channels, workflow panes, RM chat, release chrome,
// corpus shell) fell through to the plain OS menu. Two menu engines, one
// per surface, and ten surfaces with none.
//
// ── the shape ─────────────────────────────────────────────────────────────
// `createCtxMenu(vocab)` returns one menu INSTANCE. The instance owns the
// singleton element, the dismissal contract (outside click, Escape, window
// blur), viewport clamping, disabled rows with their WHY, danger rows, and
// submenus. Two instances exist in the product — the Helm's `.glass`/`.gitem`
// chrome and the Bridge home's `.hc-menu`/`.hc-menu-item` chrome — because
// the two clients are separate bundles with separate stylesheets, and the
// vocabulary is the ONLY thing that differs. The behaviour is one
// implementation.
//
// ── the default menu ──────────────────────────────────────────────────────
// A surface with no bespoke actions still attaches: `attach(root)` with no
// provider gets `defaultCtxItems`, which offers exactly the two text actions
// the native menu offered on non-editable content (Copy selection / Copy).
// That is the honest floor — replacing the OS menu must never REMOVE
// capability — and it means "this surface has no interesting actions yet" is
// a reason to wire the primitive, not a reason to leak the native menu.
//
// When neither the provider nor the default yields a single row, the handler
// returns WITHOUT preventDefault and the native menu stands: an empty custom
// menu is worse than the OS one (the Bridge home's admin-grid learned this
// first, and its comment is the precedent). That is the ONE case besides
// editable text where the OS menu survives, and primitives-check names it as
// a declared exception rather than pretending it does not exist.
//
// ── coverage ──────────────────────────────────────────────────────────────
// Each surface attaches the FALLBACK once at boot — `attach(document)` with
// no provider — so there is no corner of the app the OS menu can reach by
// nobody having thought about it. Views that want rows attach their own
// provider on their own root; the inner attach wins because it has already
// called preventDefault by the time the event bubbles (see NESTING in
// attach()). Ten Helm surfaces gained a menu that way in PR-3b without ten
// edits, and primitives-check counts the boot-path attaches, not a hand-kept
// roster of view files that would rot the first time a view was renamed.

export interface CtxMenuItem<C = unknown> {
  label: string;
  /** Sprite symbol id. Optional: a text-only row is legal in a menu (unlike a
   *  button, whose glyph IS its name). */
  glyph?: string;
  run?: (ctx: C) => void;
  /** Opens a submenu anchored on the row. */
  submenu?: (ctx: C, anchor: HTMLElement) => void;
  /** Disabled rows paint muted, never run, and carry the WHY: `note` is the
   *  inline annotation ("Up to date"), `tip` the hover title. */
  disabled?: boolean;
  note?: string;
  tip?: string;
  /** Destructive row. Rides the destructive INTENT (data-btn), so the ONE
   *  confirm-destructive interaction arms it exactly as it arms a button. */
  danger?: boolean;
  /** The question the armed state asks (defaults to `<label>?`). */
  confirm?: string;
  sep?: boolean;
}

/** The one axis the primitive is parameterised on: what a surface CALLS its
 *  menu chrome. Everything else is one implementation. */
export interface CtxMenuVocabulary {
  /** DOM id for the singleton menu element. */
  id: string;
  /** DOM id for the singleton submenu element (omit for no submenu support). */
  subId?: string;
  /** Class on the menu element. */
  menuClass: string;
  /** Class toggled to show the menu ('show' on the Helm, 'on' on the home). */
  openClass: string;
  /** Class on a row. */
  itemClass: string;
  /** Class on a separator. */
  sepClass: string;
  /** Class on the row's label span (omit to append bare text). */
  labelClass?: string;
  /** Class on the row's inline note span. */
  noteClass?: string;
  /** Class added to a danger row. */
  dangerClass?: string;
  /** Class added to a row that opens a submenu. */
  hasSubClass?: string;
  /** Renders a sprite glyph as an HTML string. */
  icon: (id: string, cls?: string) => string;
  /** Sprite id for the submenu caret (omit for none). */
  subCaretGlyph?: string;
  /** Class for the submenu caret. */
  subCaretClass?: string;
  /** Fallback menu width/height for the clamp before layout is measurable. */
  clampW?: number;
  clampH?: number;
}

// ------------------------------------------------------- the editable ruling

/**
 * The editable-text contexts the ruling exempts. `select` is here because a
 * native select's menu is the OS control's own, and `[contenteditable]`
 * covers the composer and every rich-text surface.
 */
export const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]';

/**
 * THE RULING, as one predicate. True when the native menu must survive.
 *
 * Two questions, not one: `closest(EDITABLE_SELECTOR)` catches the element
 * itself and its editable ancestors, and `isContentEditable` catches INHERITED
 * editability — a `<span>` inside a contenteditable div matches neither the
 * attribute selector nor any attribute of its own, and a check that only asked
 * the first question would strip the native menu from the middle of every
 * composer.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as (HTMLElement & { isContentEditable?: boolean }) | null;
  if (!el) return false;
  if (el.isContentEditable === true) return true;
  if (typeof el.closest !== 'function') return false;
  return el.closest(EDITABLE_SELECTOR) !== null;
}

// ------------------------------------------------------------ default menu

/** Plain-text clipboard write, shared by every Copy row. The async clipboard
 * API is absent on non-secure origins (a Bridge served over plain http), so
 * the hidden-textarea lane is a REAL branch, not dead code. Returns whether a
 * lane accepted. */
export async function copyPlainText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* blocked — try the textarea lane */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** The current selection's text, but only when the whole selection sits
 *  inside `root` (a selection that spans out of the right-clicked surface is
 *  not that surface's to offer). */
export function selectionWithin(root: Element | Document | null): string {
  if (!root) return '';
  const sel = typeof window !== 'undefined' && typeof window.getSelection === 'function'
    ? window.getSelection() : null;
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return '';
  if (!root.contains(sel.anchorNode) || !root.contains(sel.focusNode)) return '';
  return sel.toString().trim();
}

/** How much of a block's text a bare "Copy" will take. Guards against a
 *  right-click on a scroll container copying a megabyte of transcript. */
const COPY_BLOCK_CAP = 20000;

/**
 * The DEFAULT menu — exactly the two text actions the native menu offered on
 * non-editable content, and nothing invented. Returns [] when there is
 * nothing to copy, which makes the caller fall through to the native menu
 * rather than showing an empty box.
 */
export function defaultCtxItems(ev: MouseEvent, root: Element | Document | null): CtxMenuItem<unknown>[] {
  const items: CtxMenuItem<unknown>[] = [];
  const sel = selectionWithin(root);
  if (sel) {
    items.push({ label: 'Copy selection', glyph: 'copy', run: () => { void copyPlainText(sel); } });
  }
  const target = ev.target as HTMLElement | null;
  const block = target?.closest?.('[data-ctx-copy], li, tr, p, pre, h1, h2, h3, h4, article, section, .card')
    ?? target ?? null;
  const blockText = (block?.getAttribute?.('data-ctx-copy') ?? block?.textContent ?? '').trim();
  if (blockText && blockText !== sel) {
    items.push({
      label: 'Copy',
      glyph: 'copy',
      run: () => { void copyPlainText(blockText.slice(0, COPY_BLOCK_CAP)); },
    });
  }
  return items;
}

// -------------------------------------------------- the listener, on its own

/**
 * THE listener and THE ruling, without a menu instance.
 *
 * A surface that renders its OWN menu DOM — the transcript menu, whose rows
 * carry a selection capture and a conditionally-hidden Bookmark, not a
 * label+glyph list — still must not register a bare `contextmenu` handler:
 * that is how the editable-text ruling gets forgotten one file at a time, and
 * how a leak becomes invisible to the gate. It attaches here instead, and
 * gets the ruling, the nesting rule and preventDefault for free.
 *
 * `handle` returns true when it opened something (the event is then marked
 * handled) and false to fall through to whatever attach is further out.
 */
export function attachCtxHandler(
  root: Element | Document,
  handle: (ev: MouseEvent) => boolean,
): () => void {
  const onCtx = (ev: Event): void => {
    const e = ev as MouseEvent;
    if (e.defaultPrevented) return;
    if (isEditableTarget(e.target)) return;
    if (handle(e)) e.preventDefault();
  };
  root.addEventListener('contextmenu', onCtx);
  return () => root.removeEventListener('contextmenu', onCtx);
}

// ---------------------------------------------------------------- instance

/**
 * A provider that opened the menu ITSELF returns this instead of a list —
 * the AG-Grid row menu resolves its context from the pointer (row index ->
 * node -> data) and then calls `open()` with the rows it built, so there is
 * nothing to hand back. The primitive still owns the listener, still applies
 * the editable-text ruling, and still calls preventDefault; only the row
 * construction happened at the call site.
 */
export const CTX_HANDLED = 'handled';

export type CtxProviderResult<C> = CtxMenuItem<C>[] | null | undefined | typeof CTX_HANDLED;

export interface CtxAttachOptions<C> {
  /** Context value handed to every row's `run` for this open. */
  ctx?: (ev: MouseEvent) => C;
  /** Suppress the default text menu — the surface offers its rows or nothing
   *  (and nothing means the native menu stands). */
  noDefault?: boolean;
}

export interface CtxMenuInstance<C = unknown> {
  /** Wire the singleton once (idempotent; every open() calls it). */
  mount(): void;
  /** Open at viewport coordinates with an explicit item list. */
  open(x: number, y: number, items: CtxMenuItem<C>[], ctx?: C): void;
  close(): void;
  /** The singleton element (null before first mount) — the test seam. */
  el(): HTMLElement | null;
  /** The shared submenu element (null when the vocabulary declares no subId). */
  subEl(): HTMLElement | null;
  /** Position + reveal the submenu at the right edge of its parent row
   *  (flips left near the viewport edge). Content is set by the caller. */
  showSub(anchor: HTMLElement): void;
  closeSub(): void;
  /**
   * ATTACH the menu to a surface. This is the seat the ruling lives in:
   * an editable-text target is left to the native menu, always.
   *
   * `provider` returns the rows for this event, or null/[] to take the
   * default text menu. Returns an unsubscribe.
   */
  attach(
    root: Element | Document,
    provider?: (ev: MouseEvent) => CtxProviderResult<C>,
    opts?: CtxAttachOptions<C>,
  ): () => void;
}

const ESC_MAP: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
export function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"]/g, (c) => ESC_MAP[c] ?? c);
}

export function createCtxMenu<C = unknown>(vocab: CtxMenuVocabulary): CtxMenuInstance<C> {
  let menu: HTMLElement | null = null;
  let sub: HTMLElement | null = null;

  // `?.` throughout: several console modules are contractually STUB-DRIVABLE
  // (helm-console/scripts/client-dom-stub.mjs), whose document implements
  // createElement and body but not getElementById — and a primitive that only
  // works against a real Document pushes every such call site into forking
  // it, which is the defect this module exists to remove. (Measured: the
  // branch-browse suite drove the overflow menu straight through this
  // function on the first run after the migration.)
  function ensure(id: string, cls: string): HTMLElement {
    let el = (typeof document.getElementById === 'function'
      ? document.getElementById(id)
      : null) as HTMLElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      el.className = cls;
      el.setAttribute?.('role', 'menu');
      (document as { body?: { appendChild?: (n: HTMLElement) => void } }).body?.appendChild?.(el);
    }
    return el;
  }

  function closeSub(): void {
    sub?.classList.remove(vocab.openClass);
  }

  function close(): void {
    menu?.classList.remove(vocab.openClass);
    if (menu) menu.hidden = true;
    closeSub();
  }

  function mount(): void {
    if (menu) return;
    menu = ensure(vocab.id, vocab.menuClass);
    menu.hidden = true;
    if (vocab.subId) sub = ensure(vocab.subId, vocab.menuClass);
    document.addEventListener?.('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.(`#${vocab.id}`)) return;
      if (vocab.subId && t?.closest?.(`#${vocab.subId}`)) return;
      close();
    });
    document.addEventListener?.('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Escape') close();
    });
    if (typeof window !== 'undefined') window.addEventListener?.('blur', close);
  }

  /** One row, built element-by-element rather than as a slab of innerHTML.
   *  Two reasons, both load-bearing: the console's stub document parses
   *  innerHTML only one element deep (a bulk `menu.innerHTML = rows.join('')`
   *  yields children with no classes and no label spans — measured on
   *  branch-browse's overflow menu), and textContent for the label/note is
   *  the only safe lane when a label can carry a user-named release, branch
   *  or file path. */
  function rowEl(it: CtxMenuItem<C>): HTMLElement {
    if (it.sep) {
      const sep = document.createElement('div');
      sep.className = vocab.sepClass;
      return sep;
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    // role="menuitem" is what a screen reader already wanted, and what
    // buttons-check counts: these are MENU ROWS, not action buttons — an
    // icon-only context menu would lose the labels that ARE the menu.
    btn.setAttribute?.('role', 'menuitem');
    const cls = [
      vocab.itemClass,
      it.danger && vocab.dangerClass ? vocab.dangerClass : '',
      it.submenu && vocab.hasSubClass ? vocab.hasSubClass : '',
    ].filter(Boolean).join(' ');
    btn.className = cls;
    for (const c of cls.split(/\s+/)) if (c) btn.classList?.add(c);
    if (it.danger) {
      // A destructive ROW rides the destructive INTENT so the ONE
      // confirm-destructive interaction arms it — the primitive treats a text
      // affordance and an icon button identically by design.
      btn.setAttribute?.('data-btn', 'destructive');
      btn.setAttribute?.('data-btn-confirm', it.confirm ?? `${it.label}?`);
    }
    const glyph = it.glyph ? vocab.icon(it.glyph) : '';
    const caret = it.submenu && vocab.subCaretGlyph
      ? vocab.icon(vocab.subCaretGlyph, vocab.subCaretClass)
      : '';
    const labelSpan = vocab.labelClass
      ? `<span class="${vocab.labelClass}"></span>`
      : '<span></span>';
    btn.innerHTML = `${glyph}${labelSpan}${caret}`;
    const seat = (vocab.labelClass
      ? btn.querySelector?.(`.${vocab.labelClass}`)
      : btn.querySelector?.('span')) as HTMLElement | null | undefined;
    if (seat) seat.textContent = it.label;
    else btn.textContent = it.label; // a document that did not parse the span
    if (it.note && vocab.noteClass) {
      const n = document.createElement('span');
      n.className = vocab.noteClass;
      n.textContent = it.note;
      btn.appendChild(n);
    }
    if (it.tip) btn.dataset.tip = it.tip;
    if (it.disabled) btn.disabled = true;
    return btn;
  }

  function open(x: number, y: number, items: CtxMenuItem<C>[], ctx?: C): void {
    mount();
    const m = menu!;
    closeSub();
    m.replaceChildren?.();
    if (!m.replaceChildren) m.innerHTML = '';
    for (const it of items) {
      const el = rowEl(it);
      m.appendChild(el);
      if (it.sep || it.disabled) continue; // disabled rows never run
      const btn = el as HTMLButtonElement;
      if (it.submenu) {
        const openSub = (): void => it.submenu!(ctx as C, btn);
        btn.addEventListener('mouseenter', openSub);
        btn.addEventListener('click', (ev) => { ev.stopPropagation?.(); openSub(); });
        continue;
      }
      btn.addEventListener('mouseenter', closeSub);
      btn.addEventListener('click', () => {
        close();
        it.run?.(ctx as C);
      });
    }

    m.hidden = false;
    m.classList.add(vocab.openClass);
    // Clamp with layout applied — offsetWidth needs the menu visible.
    const w = m.offsetWidth || vocab.clampW || 240;
    const h = m.offsetHeight || vocab.clampH || 300;
    const vw = typeof window !== 'undefined' ? (window.innerWidth || 1280) : 1280;
    const vh = typeof window !== 'undefined' ? (window.innerHeight || 800) : 800;
    if (m.style) {
      m.style.left = `${Math.max(8, Math.min(x, vw - w - 8))}px`;
      m.style.top = `${Math.max(8, Math.min(y, vh - h - 8))}px`;
    }
  }

  function showSub(anchor: HTMLElement): void {
    if (!sub || !menu) return;
    sub.classList.add(vocab.openClass); // measure with layout applied
    const ar = anchor.getBoundingClientRect();
    const sw = sub.offsetWidth || vocab.clampW || 236;
    const sh = sub.offsetHeight || 200;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    let left = ar.right - 4;
    if (left + sw > vw - 8) left = ar.left - sw + 4; // flip left
    const top = Math.min(ar.top - 6, vh - sh - 8);
    sub.style.left = `${Math.max(8, left)}px`;
    sub.style.top = `${Math.max(8, top)}px`;
  }

  function attach(
    root: Element | Document,
    provider?: (ev: MouseEvent) => CtxProviderResult<C>,
    opts: CtxAttachOptions<C> = {},
  ): () => void {
    // NESTING is handled by attachCtxHandler: a view attaches its own rows on
    // its own root, and every surface attaches the FALLBACK on its document,
    // so no corner of the app is left to the OS menu. Both handlers see the
    // same bubbling event and the inner one wins by having already called
    // preventDefault — no registry, no ordering rule, and no stopPropagation
    // (which would break the drag/close handlers sharing these elements).
    return attachCtxHandler(root, (e) => {
      const supplied = provider?.(e);
      if (supplied === CTX_HANDLED) return true;
      const items = (supplied && supplied.length)
        ? supplied
        : (opts.noDefault ? [] : (defaultCtxItems(e, root) as CtxMenuItem<C>[]));
      // Nothing to offer under the pointer: leave the native menu standing
      // rather than showing an empty box.
      if (!items.length) return false;
      open(e.clientX, e.clientY, items, opts.ctx?.(e));
      return true;
    });
  }

  return {
    mount, open, close, showSub, closeSub, attach,
    el: () => menu,
    subEl: () => sub,
  };
}
